// PURPOSE: Session-local list position and view memory. CONTRACT: spec 078 A3, restore only on POP. DEPENDS: React Router, sessionStorage. CEILING: small JSON UI state, never records or credentials.
import { useCallback, useLayoutEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
type Memory<T> = { state: Partial<T>; scroll: number };
const pending=new Map<string,number>();
const storageKey=(key:string)=>`velox.list.${key}`;
function read<T extends object>(key:string):Memory<T>|undefined {
 try {
  const value=JSON.parse(sessionStorage.getItem(storageKey(key)) || 'null');
  if(value && value.state && typeof value.state==='object' && !Array.isArray(value.state) && Number.isFinite(value.scroll) && value.scroll>=0)return value;
 } catch { /* Storage is optional, including malformed or unavailable browser storage. */ }
}
function write<T extends object>(key:string,memory:Memory<T>) {
 try { sessionStorage.setItem(storageKey(key),JSON.stringify(memory)); } catch { /* Quota or privacy settings must not block a list. */ }
}
function scroller():HTMLElement|undefined {
 const main=document.getElementById('main-content');
 if(window.innerWidth<=600 && (!main || main.scrollHeight<=main.clientHeight || !/^(auto|scroll)$/.test(getComputedStyle(main).overflowY)))return;
 return main || undefined;
}
const scrollPosition=()=>scroller()?.scrollTop ?? window.scrollY;
// Call after list data has rendered. Only a mounted POP memory can arm this one-shot restore.
export function scrollToRemembered(key:string):void {
 const top=pending.get(key);if(top===undefined)return;
 const main=scroller();if(!main && window.innerWidth>600)return;
 if(main)main.scrollTop=top;else window.scrollTo({top,behavior:'instant'});
 pending.delete(key);
}
// State starts as {} on PUSH/REPLACE. Use state.page ?? default; call remember before opening a record.
// Unmount/pagehide also save. restore() re-reads on POP; scrollToRemembered(key) waits for loaded rows.
export function useListMemory<T extends object = Record<string,unknown>>(key:string):{
 state:Partial<T>;setState:Dispatch<SetStateAction<Partial<T>>>;remember:()=>void;restore:()=>Partial<T>|undefined;
} {
 const location=useLocation(),navigation=useNavigationType();
 const [,render]=useState(0);
 const session=useMemo(()=>{
  const saved=navigation==='POP'?read<T>(key):undefined;
  return {state:saved?.state || {} as Partial<T>,scroll:saved?.scroll ?? 0,saved,navigation};
 },[key,location.key,navigation]);
 const setState=useCallback<Dispatch<SetStateAction<Partial<T>>>>(update=>{
  session.state=typeof update==='function'?update(session.state):update;render(value=>value+1);
 },[session]);
 const remember=useCallback(()=>{session.scroll=scrollPosition();write(key,{state:session.state,scroll:session.scroll});},[key,session]);
 const restore=useCallback(()=>{
  if(session.navigation!=='POP')return;
  const saved=read<T>(key);if(!saved)return;
  session.state=saved.state;session.scroll=saved.scroll;pending.set(key,saved.scroll);render(value=>value+1);return saved.state;
 },[key,session]);
 useLayoutEffect(()=>{
  if(session.saved)pending.set(key,session.saved.scroll);else pending.delete(key);
  const main=document.getElementById('main-content');
  const track=()=>{session.scroll=scrollPosition();};
  main?.addEventListener('scroll',track);window.addEventListener('scroll',track);window.addEventListener('pagehide',remember);
  return()=>{
   // The shell keys main by pathname; use the last observed offset after that node detaches.
   if(main?.isConnected)session.scroll=scrollPosition();
   write(key,{state:session.state,scroll:session.scroll});pending.delete(key);
   main?.removeEventListener('scroll',track);window.removeEventListener('scroll',track);window.removeEventListener('pagehide',remember);
  };
 },[key,session,remember]);
 return {state:session.state,setState,remember,restore};
}
